import { create } from "zustand";
import { persist } from "zustand/middleware";
import { client } from "../lib/api";

export type AuthUser = {
  id: string;
  email: string;
  name?: string | null;
  role: "trainer" | "customer";
  emailVerified: boolean;
};

interface AuthState {
  user: AuthUser | null;
  activeProfileId: string | null; // null = logged in as self, string = acting as customer
  loading: boolean;
  redirectPath: string | null; // Path to redirect to after login
  setUser: (user: AuthUser | null) => void;
  switchProfile: (customerId: string | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkSession: () => Promise<void>;
  handleUnauthorized: () => void;
  getRedirectPath: () => string;
  clearRedirectPath: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      activeProfileId: null,
      loading: true,
      redirectPath: null,

      setUser: (user) => set({ user }),

      switchProfile: (customerId) => {
        set({ activeProfileId: customerId });
      },

      signIn: async (email, password) => {
        await client.default.signInEmail({
          requestBody: { email, password },
        });
        const responseAuthorization =
          await client.authorization.getAuthorizationSession();
        set({
          user: responseAuthorization.user as AuthUser | null,
          activeProfileId: null,
        }); // Reset to self on login
      },

      signUp: async (email, password, name) => {
        await client.default.signUpWithEmailAndPassword({
          requestBody: { email, password, name },
        });
        const responseAuthorization =
          await client.authorization.getAuthorizationSession();
        set({
          user: responseAuthorization.user as AuthUser | null,
          activeProfileId: null,
        });
      },

      signOut: async () => {
        try {
          await client.default.signOut({ requestBody: {} });
        } catch (error) {
          // Ignore errors, clear local state anyway
          console.error("Sign out error:", error);
        }
        set({ user: null, activeProfileId: null, redirectPath: null });
        sessionStorage.removeItem("redirectAfterLogin");
      },

      checkSession: async () => {
        console.log(
          "checkSession: Starting session check, loading:",
          get().loading,
        );

        // Set a timeout to force loading to false if the check hangs
        const timeoutId = setTimeout(() => {
          console.log("checkSession: Timeout - forcing loading to false");
          set({ loading: false });
        }, 3000);

        try {
          const { user } = await client.authorization.getAuthorizationSession();
          clearTimeout(timeoutId);
          console.log("checkSession: Session valid, user:", user?.email);
          set({ user, loading: false });
        } catch (error) {
          clearTimeout(timeoutId);
          console.log("checkSession: Session check failed", error);
          // Don't redirect here, let the route protection handle it
          set({ user: null, loading: false, activeProfileId: null });
        }
      },

      handleUnauthorized: () => {
        console.log("Handling unauthorized - clearing user state");
        set({ user: null, activeProfileId: null, loading: false });
      },

      getRedirectPath: () => {
        const stored = sessionStorage.getItem("redirectAfterLogin");
        return stored || get().redirectPath || "/";
      },

      clearRedirectPath: () => {
        set({ redirectPath: null });
        sessionStorage.removeItem("redirectAfterLogin");
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        activeProfileId: state.activeProfileId,
        redirectPath: state.redirectPath,
      }),
    },
  ),
);
